import React from 'react';
import { ShoppingCart, Download, Package, CheckCircle, Star } from 'lucide-react';
import { soundKits } from '../mock';
import { useCart } from '../context/CartContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';

const SoundKits = () => {
  const { addToCart } = useCart();
  const { currentTheme } = useTheme();

  const handleAddToCart = (kit) => {
    addToCart({ ...kit, type: 'soundkit' });
    toast({
      title: "Added to cart",
      description: `${kit.name} has been added to your cart.`,
    });
  };

  return (
    <div className="min-h-screen bg-black pt-24 pb-32">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-16 h-16 rounded-xl flex items-center justify-center animate-pulse"
                 style={{ backgroundColor: `${currentTheme.primary}20` }}>
              <Package className="w-8 h-8" style={{ color: currentTheme.primary }} />
            </div>
            <div>
              <h1 className="text-5xl md:text-6xl font-bold text-white uppercase">Sound Kits</h1>
            </div>
          </div>
          <p className="text-xl text-gray-400">
            My signature sounds - the exact production tools used in platinum hits
          </p>
        </div>

        {/* Featured Banner */}
        <div className="mb-16 p-8 rounded-2xl relative overflow-hidden"
             style={{ background: `linear-gradient(135deg, ${currentTheme.primary}20, transparent)` }}>
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-4">
              <Star className="w-6 h-6" style={{ color: currentTheme.primary }} />
              <span className="font-bold text-white uppercase tracking-wider">Producer's Choice</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Professional Sound Kits by Tha Advocate Beats
            </h2>
            <p className="text-gray-300 max-w-2xl">
              These are the exact sounds, drums, and melodies I use in my productions. Industry-standard quality, 
              royalty-free, and ready to elevate your beats to the next level.
            </p>
          </div>
        </div>

        {/* Sound Kits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {soundKits.map(kit => (
            <div 
              key={kit.id}
              className="group bg-zinc-900 rounded-2xl overflow-hidden border border-white/10 hover:border-white/30 transition-all duration-300 hover:transform hover:scale-105"
            >
              <div className="relative aspect-video overflow-hidden">
                <img 
                  src={kit.imageUrl} 
                  alt={kit.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                
                {kit.badge && (
                  <div className="absolute top-4 right-4">
                    <span className="px-4 py-2 rounded-full text-sm font-bold bg-black text-white animate-pulse"
                          style={{ border: `2px solid ${currentTheme.primary}` }}>
                      {kit.badge}
                    </span>
                  </div>
                )}

                <div className="absolute bottom-4 left-4">
                  <div className="px-3 py-1 rounded-full text-xs font-bold bg-black/70 backdrop-blur-sm text-white">
                    {kit.category}
                  </div>
                </div>
              </div>

              <div className="p-6">
                <h3 className="text-2xl font-bold text-white mb-3">{kit.name}</h3>
                <p className="text-gray-400 mb-4 leading-relaxed">{kit.description}</p>

                {/* Contents */}
                <div className="mb-4 p-4 rounded-lg bg-black/30 border border-white/5">
                  <div className="text-sm font-bold text-white mb-2 uppercase tracking-wider">
                    <Download className="w-4 h-4 inline mr-2" />
                    Includes:
                  </div>
                  <div className="space-y-1">
                    {kit.contents.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-gray-400">
                        <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: currentTheme.primary }} />
                        {item}
                      </div>
                    ))}
                  </div>
                </div>

                {/* File Info */}
                <div className="flex gap-4 mb-4 text-sm text-gray-500">
                  <div>
                    <span className="font-medium">Format:</span> {kit.format}
                  </div>
                  <div>
                    <span className="font-medium">Size:</span> {kit.fileSize}
                  </div>
                </div>

                {/* Price & Action */}
                <div className="flex items-center justify-between pt-4 border-t border-white/10">
                  <div>
                    {kit.originalPrice && (
                      <div className="text-sm text-gray-500 line-through mb-1">
                        ${kit.originalPrice}
                      </div>
                    )}
                    <div className="text-3xl font-bold text-white">
                      ${kit.price}
                    </div>
                  </div>
                  <Button
                    onClick={() => handleAddToCart(kit)}
                    className="h-12 px-6 text-black font-bold hover:scale-105 transition-transform active:scale-95"
                    style={{ backgroundColor: currentTheme.primary }}
                  >
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    Add to Cart
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Info Section */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              icon: CheckCircle,
              title: 'Instant Download',
              description: 'Get immediate access after purchase. Start producing in minutes.'
            },
            {
              icon: Star,
              title: 'Royalty-Free',
              description: 'Use in unlimited projects. Keep 100% of your royalties.'
            },
            {
              icon: Package,
              title: 'Professional Quality',
              description: 'Studio-grade samples used in platinum productions.'
            }
          ].map((feature, idx) => (
            <div 
              key={idx}
              className="p-6 rounded-xl bg-zinc-900 border border-white/10 text-center"
            >
              <div className="w-12 h-12 rounded-lg mx-auto mb-4 flex items-center justify-center"
                   style={{ backgroundColor: `${currentTheme.primary}20` }}>
                <feature.icon className="w-6 h-6" style={{ color: currentTheme.primary }} />
              </div>
              <h3 className="text-white font-bold mb-2">{feature.title}</h3>
              <p className="text-gray-400 text-sm">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SoundKits;